import pako from 'pako';

import { downloadObject, queryNoTracking } from './DataService';
import { accentUserLang, accentUtils, from, insyteDate, showWait, t } from './HelperService'
import { getUser } from './UserService';
import { selectProduct } from '../dialogs/ProductSelectionDlg';
import { pricingAccountsInUse, query_UnitTypes } from '../../PermanentQueries';




const query_ProductList = queryNoTracking("ProductList");
const query_Product_BUExclusions = queryNoTracking("Product_BUExclusions");

// The single place an order instant (a JS Date / date-time string such as the quote expiry) becomes a
// calendar day for the availability rules: it is the browser user's calendar day of that instant, the
// same day the server resolves for this user via the insytetz header. The configurator itself only
// ever sees Bridge InsyteDate values (it compares .dayNumber and would silently fail on a JS Date).
const toOrderDateOptions = (orderDateOptions) => {
    const options = orderDateOptions ?? {};

    return window.InsyteConfig.OrderDateOptions.From(
        insyteDate.toBridge(options.effectiveDate),
        insyteDate.toBridge(options.effectiveStockDate),
        insyteDate.toBridge(options.availabilityWarningDate)
    );
};


const toFixedPicklist = (picklist) => {
    picklist.values = Object.keys(picklist).map(k => picklist[k]);
    picklist.getPicklistSource = () => picklist.values.map(v => { return { ID: v, Description: t(v) }; });
};

export const optionDisplayTypes = {
    Picklist: "Picklist",
    Numeric: "Numeric",
    Decimal: "Decimal",
    Checkbox: "Checkbox",
    Text: "Text",
    Length: "Length",
    LinearMetreCuts: "Linear Metre Cuts",
    Fabric: "Fabric",
    Fabric2Line: "Fabric 2 Line",
    PanelPattern: "Panel Pattern",
    FabricInline: "Fabric Inline",
    Fabric2LineInline: "Fabric 2 Line Inline",
};

toFixedPicklist(optionDisplayTypes);

// Display-only labels for the Display Type dropdown, from the
// application_strings.optionDisplayTypes string section (keyed by the persisted
// values, so e.g. the fabric pickers can read "Fabric (Library)"). The IDs stay
// the exact persisted values — never change those. defaultValue keeps a missing
// entry rendering as the raw value instead of a dotted i18n key.
optionDisplayTypes.getPicklistSource = () =>
    optionDisplayTypes.values.map(v => ({
        ID: v,
        Description: t(`application_strings.optionDisplayTypes.${v}`, { defaultValue: v })
    }));

let products = [];
let productActiveVersions = {};
window.productList = products;
let productBUExclusions = [];
let unitTypes = [];

let productDictionary = null;

export function configFrom(collection) {
    return window.System.Linq.Enumerable.from(collection);
}

export function getUnitTypes() {
    return unitTypes;
}

export async function refreshProductExclusionList() {
    productBUExclusions = await query_Product_BUExclusions.getAll();
    unitTypes = await query_UnitTypes.getAll();
}

function setActiveVersions() {

    products.filter(p => p.IsActiveVersion === true).map(p => {
        productActiveVersions[p.ID.toUpperCase()] = p.VersionID;
    });

}
function getProductCacheKey(user) {
    return `productList_${user.ClientID}`;
}

export function loadLocallyCachedProducts(user) {

    const localStoredProducts = localStorage.getItem(getProductCacheKey(user));


    if (accentUtils.isEmpty(products)) {
        products = accentUtils.isNull(localStoredProducts) ? [] : JSON.parse(localStoredProducts);
        window.productList = products;
        setActiveVersions();
    }
    

}

export async function refreshProducts(clearServerCache, showWaitIndicator = false) {


    const done = showWaitIndicator ? showWait() : () => Promise.resolve();

    modelCache = {};

    productDictionary = null;

    products = await query_ProductList.getAll({ clearCache: clearServerCache });

    const user = getUser();

    window.productList = products;
    setActiveVersions();

    window.setTimeout(() => {
        localStorage.setItem(getProductCacheKey(user), JSON.stringify(products));
    }, 200);

    await done();

    return products;
}

export function getProduct(id, versionID) {



    const idUpper = id?.toUpperCase() ?? null;

    if (accentUtils.isNull(versionID)) {
        versionID = productActiveVersions[idUpper];
    }


    if (accentUtils.isNull(idUpper)) return null;

    const versionUpperID = versionID?.toUpperCase() ?? null;



    if (accentUtils.isNull(productDictionary)) {

        productDictionary = {};

        const products = getProducts();

        for (var i = 0; i <  products.length; i++) {

            const currProduct = products[i];
            const currProductID = currProduct.ID.toUpperCase();

            if (!productDictionary[currProductID]) {
                productDictionary[currProductID] = [];
            }

            productDictionary[currProductID].push(currProduct);

        }

    }


    const productVersions = productDictionary[idUpper] ?? [];


    if (versionUpperID) {
        return from(productVersions).where(v => (v.VersionID ?? accentUtils.getEmptyGuid()).toUpperCase() === versionUpperID).firstOrDefault();
    } else {
        return from(productVersions).orderBy(v => v.IsActiveVersion).thenBy(v => v.IsDraft).firstOrDefault();
    }

    
}

export function getProducts() {
    return [...products];
}

// Whether a job/order line is on a product this system receives through the
// marketplace. Marketplace product rows carry no local version id, so the lookup
// falls back to any version of the product id when the line's version is unknown.
export function isMarketplaceProductLine(line) {
    if (accentUtils.isNull(line?.ProductID)) return false;

    const product = getProduct(line.ProductID, line.ProductVersionID) ?? getProduct(line.ProductID, null);

    return product?.IsMarketplaceProduct === true;
}

// Mirrors InsyteConfig.OptionHideFromRules.IsHiddenForDisplay for the HideFrom
// snapshot on an order line option (OrderLineOptionsData): "Everybody" = hidden on
// every system, "Others" = hidden on marketplace recipients only. Null (or anything
// unrecognised) on blobs written before the setting existed, or when the product
// never set it, = shown.
export const optionHideFrom = Object.freeze({ none: "None", others: "Others", everybody: "Everybody" });

// Picklist for the group/option configure forms. IDs are the persisted strings
// (InsyteConfig.OptionHideFromValues); a saved null renders as blank, which means "don't hide".
export const getOptionHideFromPicklistSource = () => [
    { ID: optionHideFrom.none, Description: t("application_strings.views.productConfig.HideFromValues.None") },
    { ID: optionHideFrom.others, Description: t("application_strings.views.productConfig.HideFromValues.Others") },
    { ID: optionHideFrom.everybody, Description: t("application_strings.views.productConfig.HideFromValues.Everybody") }
];

export function isOLOHiddenByProduct(olo, isMarketplaceProduct) {
    const hideFrom = olo?.HideFrom;

    if (accentUtils.isNull(hideFrom)) return false;
    if (hideFrom === optionHideFrom.everybody) return true;

    return hideFrom === optionHideFrom.others && isMarketplaceProduct === true;
}



export function productVersionDisplayText(product) {

    if (product.IsDraft) return "DRAFT";

    return `v${product.Version??1}`;

}
export function productVersionSort(product) {

    if (product.IsDraft) return -1;

    const num = Number(product.Version);

    if (isNaN(num)) {
        return 0;
    }

    return 99999999999 - num;
}



export function allowMemberProductSeperation(productID, memberConfigID) {
    return from(getProduct(productID)?.MemberProducts ?? []).any(m => (!m.Required && m.CanBeSeparated) && m.ID.toUpperCase() === memberConfigID?.toUpperCase());
}

export const ConfigNotifications = {
    SizeChanging: window.InsyteConfig.InsyteConfigEvents.SizeChanging,
    SizeChangeOccurred: window.InsyteConfig.InsyteConfigEvents.SizeChangeOccurred
};


export async function getProductConfig(lineEntity, olos, isReadOnly, isQuote, onSave, onChanged, ignoreSizes, companyInfo, onNewOrderLineOption, businessUnitID, companyID, orderDateOptions) {

    const res = await loadProductConfig(lineEntity, olos, isReadOnly, isQuote, onSave, onChanged, ignoreSizes, companyInfo, onNewOrderLineOption, businessUnitID, companyID, orderDateOptions);

    return res;

}

// One request per job / supply job serves every saved line of a multi-line view. The query
// cache is cleared on navigation (AccentRouter) like every other cached query; the job and
// supply job views also clear it on mount so the remount after a save picks up a changed
// pricing account rather than the rows fetched before it.
const query_Line_AccountKeywordValues = queryNoTracking("Line_AccountKeywordValues", 0);

export const clearLineAccountKeywordCache = () => query_Line_AccountKeywordValues.clearCache();

function fetchAccountKeywordRows(params) {

    return query_Line_AccountKeywordValues.getAllWithCache(params)
        .then(rows => (rows ?? []).map(r => ({
            LineID: fromInt(r?.LineID),
            Keywords: (r?.Keywords ?? [])
                .filter(k => !accentUtils.isEmpty(k?.Key))
                .map(k => ({ Key: k.Key, Value: k.Value ?? "" }))
        })))
        .catch(e => {
            console.warn("Line_AccountKeywordValues failed; configuring without account keywords", e);
            clearLineAccountKeywordCache();
            return [];
        });
}

function keywordsOf(row) {
    return accentUtils.isNull(row) || row.Keywords.length === 0 ? null : row.Keywords;
}

/**
 * The supplier host keywords (&PRICEAS, &#STRGRP, ...) of the pricing account governing a
 * job / supply job line, as [{ Key, Value }] for OrderCompanyInfo.KeywordValues. The server
 * resolves the account the same way pricing stamps the line. Saved lines are served from
 * one per-job request (every line's row plus the item-level default); an unsaved line with
 * its own supplier or account stamp is resolved individually. Null when the line belongs to
 * no job/order (multi-config, previews), the tenant runs no pricing accounts, or no account
 * resolves - the configurator then runs without seeded keywords, exactly as before.
 */
export async function getLineAccountKeywordValues(lineEntity) {

    const jobID = fromInt(lineEntity?.JobID);
    const orderID = accentUtils.isNull(jobID) ? fromInt(lineEntity?.OrderID) : null;

    if (accentUtils.isNull(jobID) && accentUtils.isNull(orderID)) return null;

    // Most tenants run no pricing accounts: one session-cached answer, no per-job request.
    if (!await pricingAccountsInUse()) return null;

    const rawLineID = fromInt(lineEntity.ID);
    const lineID = accentUtils.isNull(rawLineID) || rawLineID <= 0 ? null : rawLineID;
    const supplierID = accentUtils.isEmpty(lineEntity.SupplierID) ? null : `${lineEntity.SupplierID}`;
    const pricingProgramAccountID = accentUtils.isEmpty(lineEntity.PricingProgramAccountID) ? null : `${lineEntity.PricingProgramAccountID}`;

    const itemParams = { jobID, orderID };

    // Unsaved line carrying its own routing: the job-wide default may not apply to it.
    if (accentUtils.isNull(lineID) && (!accentUtils.isNull(supplierID) || !accentUtils.isNull(pricingProgramAccountID))) {
        const rows = await fetchAccountKeywordRows({ ...itemParams, lineID: null, supplierID, pricingProgramAccountID });
        return keywordsOf(rows[0]);
    }

    const jobRows = await fetchAccountKeywordRows({ ...itemParams, lineID: null, supplierID: null, pricingProgramAccountID: null });

    if (accentUtils.isNull(lineID)) {
        return keywordsOf(jobRows.find(r => accentUtils.isNull(r.LineID)));
    }

    const lineRow = jobRows.find(r => r.LineID === lineID);
    if (!accentUtils.isNull(lineRow)) return keywordsOf(lineRow);

    // Saved after the job-wide rows were fetched: resolve this one line directly.
    const rows = await fetchAccountKeywordRows({ ...itemParams, lineID, supplierID: null, pricingProgramAccountID: null });
    return keywordsOf(rows[0]);
}

/**
 * Starts the line's product model download so it runs alongside other per-line lookups
 * (the account keywords) instead of after them; loadProductConfig then finds it in the
 * model cache. Fire-and-forget: failures surface on the real load.
 */
export function prewarmLineProductModel(lineEntity) {

    if (accentUtils.isNull(lineEntity) || !accentUtils.isNull(lineEntity.__previewModel) || accentUtils.isEmpty(lineEntity.ProductID)) return;

    const product = getProduct(lineEntity.ProductID);
    if (accentUtils.isNull(product)) return;

    const versionID = product.IsMultiConfig ? lineEntity.MultiProductVersionID : lineEntity.ProductVersionID;

    loadProductModel(lineEntity.ProductID, versionID).catch(() => { });
}

function fromInt(value) {
    if (accentUtils.isEmpty(value)) return null;
    const n = Number(value.toString());
    return Number.isFinite(n) ? n : null;
}

let multiConifigFakeLineID = -999999;
export async function getMultiConfigConfig(productID, versionID, orderDateOptions) {

    return await getProductConfig(
        { ID: multiConifigFakeLineID--, ProductID: productID, MultiProductVersionID: versionID },
        "[]",
        true,
        true,
        () => { }, //save olos
        () => { },
        true,
        null,
        null,
        null,
        null,
        orderDateOptions);

}

function loadProductConfig(lineEntity, olos, isReadOnly, isQuote, onSave, onChanged, ignoreSizes, companyInfo, onNewOrderLineOption, businessUnitID, companyID, orderDateOptions) {

    window.InsyteConfig.DateToStringProvider.Local = accentUserLang;
    const dateOptions = toOrderDateOptions(orderDateOptions);

    return new Promise(p => {

        window.InsyteHTML.Apps.Configurator.Configurator_App.GetConfigModel(
            pID => {

                // Preview lines carry their model inline (the AI product author's
                // test drive over an UNSAVED definition) — never resolve them
                // against published versions or the model cache.
                if (!accentUtils.isNull(lineEntity.__previewModel)) return Promise.resolve(lineEntity.__previewModel);

                const product = getProduct(pID);

                const isMultiConfig = product.IsMultiConfig;

                const versionID = (isMultiConfig ? lineEntity.MultiProductVersionID : lineEntity.ProductVersionID);


                return loadProductModel(pID, versionID);
            },                    
            res => {
                p(res);
            },
            lineEntity,
            olos,
            isReadOnly,
            isQuote,
            onSave,
            onChanged,
            ignoreSizes,
            companyInfo,
            onNewOrderLineOption,
            `${(businessUnitID ?? 1)}`,
            `${(companyID ?? "")}`,
            dateOptions
        )

    });


}


//export function reloadConfigurator(id, lineEntity, olos, isReadOnly, isQuote, onSave, onChanged, onLoaded, ignoreSizes, companyInfo) {

//    window.InsyteHTML.Apps.Configurator.Configurator_App.ReloadConfigTRL(
//        lineEntity,
//        olos,
//        isReadOnly,
//        isQuote,
//        onSave,
//        onChanged,
//        onLoaded,
//        ignoreSizes,
//        companyInfo,
//        id
//    )

//}



export function createDiscountNodes(jobGuidID, products, reference) {
    return window.InsyteProduct.InsyteWeb.DiscountTreeHelper.CreateDiscountViewModel(jobGuidID, reference, products.Costs, products.Retail);
}



export function InsyteHtml() {
    var tt = 4;
}

let modelCache = {};
const modelLoadsInFlight = {};


function getModelCacheKey(productID, versionID) {
    return `${productID}_${versionID}`;

}
export function removeFromModelCache(productID, versionID) {
    modelCache[getModelCacheKey(productID, versionID)] = null;
}


async function downloadProductJson(product) {
    const data = await makeCorsRequest(product.ModelURI, product.ModelIsCompressed);


    const output = product.ModelIsCompressed ? pako.inflate(data) : data;

    const file = new TextDecoder('utf-8').decode(output);

    return file;
}


export async function loadProductJson(productID, versionID) {

    if (accentUtils.isNull(versionID)) {
        versionID = productActiveVersions[productID.toUpperCase()];
    }

    var product = getProduct(productID, versionID);


    if (!accentUtils.isNull(product)) {


        const file = await downloadProductJson(product);


        return JSON.parse(file);

    }

    return null;
}

/**
 * Loads a configurator data model from a PREVIEW model object (the unsaved AI-authored
 * definition compiled server-side into the exact shape of a published model blob:
 * { ID, Model, ModelAuto }). Nothing touches the model cache — the caller stashes the
 * result on its fake line as __previewModel, which loadProductConfig serves directly.
 */
export function loadPreviewProductModel(model) {

    window.InsyteHTML.Apps.Configurator.Configurator_App.Init();

    return {
        productID: model.ID,
        dataModel: window.InsyteHTML.Apps.Configurator.RTL.ModelLoader.LoadDataModel(model),
    };
}

export async function loadProductModel(productID, versionID) {


    if (accentUtils.isNull(versionID)) {
        versionID = productActiveVersions[productID.toUpperCase()];
    }

    const key = getModelCacheKey(productID, versionID);


    if (!accentUtils.isNull(modelCache[key])) return modelCache[key];

    // Concurrent callers (a multi-line view, or the prewarm racing the real load) share
    // one download instead of each fetching and parsing the model.
    if (!accentUtils.isNull(modelLoadsInFlight[key])) return await modelLoadsInFlight[key];

    const load = (async () => {

        //const prodList = (products.length > 0) ? products : await refreshProducts();

        var product = getProduct(productID, versionID);

        if (accentUtils.isNull(product)) return null;

        const file = await downloadProductJson(product);

        const model = JSON.parse(file);

        window.InsyteHTML.Apps.Configurator.Configurator_App.Init();

        const dataModel = window.InsyteHTML.Apps.Configurator.RTL.ModelLoader.LoadDataModel(model);

        modelCache[key] = dataModel;

        return dataModel;
    })();

    modelLoadsInFlight[key] = load;

    try {
        return await load;
    } finally {
        delete modelLoadsInFlight[key];
    }
}

export async function downloadProduct(productID, productVerionID) {


    const product = getProduct(productID, productVerionID);

    if (!accentUtils.isEmpty(product.ModelURI)) {

        const productModel = await downloadProductJson(product);
        await downloadObject(productModel, `Model_${product.ProductName}.json`);

    }


    const definition = await queryNoTracking("Product_Definition").getFirstOrDefault({ productID: product.IntID, productVerionID : productVerionID});

    await downloadObject(definition.ProductDefinition, `Definition_${product.ProductName}.json`);

}


export function getUserProductList({
    includeProductID,
    includeProductVersionID,
    includeInActive,
    includeCannotSell,
    includeDraft,
    includeExclusions,
    includeAllMemberProducts,
    includeMultiProducts,
    filterBySupplierID,
    exclusionBusinessUnitID,
    excludeMarketplaceProducts,
    excludeNoModelURI,
    excludeEmpty,
    filterByProductID,
    onlyServiceProducts
}) {

    if (accentUtils.isNull(includeProductID)) {
        includeProductID = '00000000-0000-0000-0000-000000000000';
    }

    if (onlyServiceProducts === undefined) {
        onlyServiceProducts = false;
    }

    if (includeMultiProducts === undefined) {
        includeMultiProducts = true;
    }

    if (excludeMarketplaceProducts === undefined) {
        excludeMarketplaceProducts = false;
    }

    if (excludeNoModelURI === undefined) {
        excludeNoModelURI = false;
    }


    var exclusions = (accentUtils.isEmpty(exclusionBusinessUnitID) || includeExclusions) ? [] : from(productBUExclusions).where(ex => ex.BusinessUnitID == exclusionBusinessUnitID).toArray();

    var additionalInclusions = includeAllMemberProducts
        ? from(products)
            .where(p => p.IsMultiConfig && p.Active && p.CanSell)
            .selectMany(p => from(p.MemberProducts).select(mp => mp.MemberProductID.toUpperCase()))
            .toArray()
            : [];


    var res =
        from(products)
            // isServiceProduct is dropped by the projection below, so service-only filtering happens on the raw product
            .where(p => !onlyServiceProducts || p.isServiceProduct === true)
            .select(function (p) {
                return {
                    ID: p.ID,
                    Active: p.Active,
                    Supplier: p.Supplier,
                    SupplierKey: p.SupplierKey,
                    ResellerMarketplaceKeys: p.ResellerMarketplaceKeys,
                    Product: p.ProductName,
                    ProductCode: p.ProductCode,
                    CanSell: p.CanSell,
                    IsMarketplaceProduct: p.IsMarketplaceProduct,
                    ModelURI: p.ModelURI,
                    Version: p.Version,
                    IsDraft: p.IsDraft,
                    VersionID: p.VersionID,
                    IsActiveVersion: p.IsActiveVersion,
                    IsMultiConfig: p.IsMultiConfig
                };
            })
            .where(p => {

                const upperID = p.ID.toUpperCase();

                const mustIncludeProduct = includeProductID && includeProductID.toUpperCase() === upperID;
                const mustIncludeProductVersion = p.VersionID && includeProductVersionID && includeProductVersionID.toUpperCase() === p.VersionID.toUpperCase();

                if (mustIncludeProduct && mustIncludeProductVersion) return true;

                if (additionalInclusions.length > 0) {

                    if (p.IsActiveVersion && additionalInclusions.includes(upperID)) {
                        return true;
                    }

                }

                if (!accentUtils.isEmpty(filterByProductID) && filterByProductID.toUpperCase() !== upperID) return false;

                if (!includeDraft && p.IsDraft) return false;

                if (!accentUtils.isEmpty(filterBySupplierID) && p.SupplierKey.toUpperCase() !== filterBySupplierID.toUpperCase()) return false;

                if (!includeInActive && !p.Active) return false;

                if (!includeCannotSell && !p.CanSell) return false;

                if (!includeExclusions && exclusions.length > 0) {

                    if (from(exclusions).any(ex => upperID === ex.ProductID.toUpperCase())) {
                        return false;
                    }
                }

                if (!includeMultiProducts && p.IsMultiConfig) {
                    return false;
                }

                if (excludeMarketplaceProducts && p.IsMarketplaceProduct) return false;

                if (excludeNoModelURI && accentUtils.isEmpty(p.ModelURI)) return false;

                return p.IsActiveVersion || (includeDraft && p.IsDraft);
            })
            .orderBy(p => p.Product?.toUpperCase())
            .thenBy(p => p.Supplier?.toUpperCase())
            .toArray();

    if (!excludeEmpty) {
        res.unshift({
            ID: '00000000-0000-0000-0000-000000000000',
            Product: "",
        });
    }
    return res;
}



// Create the XHR object.
function createNonCORSRequest(method, url) {
    var xhr = new XMLHttpRequest();
    xhr.responseType = "arraybuffer";
    xhr.open(method, url);
    return xhr;
}

// Create the XHR object.
function createCORSRequest(method, url) {
    var xhr = new XMLHttpRequest();
    xhr.responseType = "arraybuffer";
    if ("withCredentials" in xhr) {
        // XHR for Chrome/Firefox/Opera/Safari.
        xhr.open(method, url, true);
    } else if (typeof window.XDomainRequest != "undefined") {
        // XDomainRequest for IE.
        xhr = new window.XDomainRequest();
        xhr.open(method, url);
    } else {
        // CORS not supported.
        xhr = null;
    }
    return xhr;
}

function makeCorsRequest(url, useCors) {

    return new Promise(p => {

        var xhr = (useCors) ? createCORSRequest('GET', url) : createNonCORSRequest('GET', url);
        if (useCors && !xhr) {
            alert('CORS not supported');
            p(null);
            return;
        }

        // Response handlers.
        xhr.onload = function () {

            var arrayBuffer = xhr.response;

            // if you want to access the bytes:
            var byteArray = new Uint8Array(arrayBuffer);
            p(byteArray);
        };

        xhr.onerror = function () {
            alert('Woops, there was an error making the request.');
            p(null);
        };

        xhr.send();


    });


    
}

export async function selectAlterProduct(existingAlterToExclude) {


    const multiConfigIDs = getProducts().filter(p => p.IsMultiConfig).map(p=>p.ID);

    if (!accentUtils.isNull(existingAlterToExclude)) {
        multiConfigIDs.push(existingAlterToExclude);
    }

    const res = await selectProduct({ selectMultiple: false, includeInActive: true, includeCannotSell: true, includeExclusions: true, excludeIDs: multiConfigIDs });

    return res?.productID;

}
